import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import { ActionLink, CloseButtonIcon, useDeviceTypes } from "halifax";
import { APP_GTM_ENTRY, APP_GTM_EXIT } from "redmond";
import { getConfigTenant, isVesperTenant } from "@capone/common";
import config from "../../utils/config";
import "./styles.scss";
import AppLogo from "./app-logo.svg";
import { APP_NAME, OPEN_CTA, SUBTITLE } from "./constants";
import { trackEvent } from "../../api/v1/trackEvent";

const BANNER_DISMISSED_KEY = "smart_app_banner_dismissed";

export interface ISmartAppBannerProps {
  className?: string;
}

export const SmartAppBanner = ({ className }: ISmartAppBannerProps) => {
  const tenant = getConfigTenant();

  const [isVisible, setIsVisible] = useState(false);
  const { matchesMobile } = useDeviceTypes();
  const isAndroid = /Android/.test(navigator.userAgent);
  const shouldShowBanner = useMemo(
    () => matchesMobile && isAndroid && !isVesperTenant(tenant),
    [matchesMobile, isAndroid, tenant]
  );

  useEffect(() => {
    if (!shouldShowBanner) {
      setIsVisible(false);
      return;
    }

    // Using local storage so if they clear their browser, it'll come back like native banner
    const wasDismissed = localStorage.getItem(BANNER_DISMISSED_KEY) === "true";

    setIsVisible(!wasDismissed);
  }, [matchesMobile, isAndroid]);

  useEffect(() => {
    if (isVisible) {
      document.body.classList.add("smart-app-banner-visible");
    } else {
      document.body.classList.remove("smart-app-banner-visible");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("smart-app-banner-visible");
    };
  }, [isVisible]);

  const handleClose = () => {
    localStorage.setItem(BANNER_DISMISSED_KEY, "true");
    setIsVisible(false);
    trackEvent({
      eventName: APP_GTM_EXIT,
      properties: {
        location: window.location.pathname,
      },
    });
  };

  const handleOpenApp = () => {
    trackEvent({
      eventName: APP_GTM_ENTRY,
      properties: {
        location: "banner",
      },
    });
    window.open(config.MOBILE_APP_LINK, "_self");
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Box className={clsx("smart-app-banner-root", className)}>
      <Box className="smart-app-banner-content">
        <Box className="icon-section">
          <img
            src={AppLogo}
            alt="Capital One Travel App"
            className="app-icon"
          />
        </Box>
        <Box className="text-section">
          <Typography variant="subtitle1" className="title">
            {APP_NAME}
          </Typography>
          <Typography variant="caption" className="description">
            {SUBTITLE}
          </Typography>
        </Box>
        <Box className="actions-section">
          <button type="button" className="open-button" onClick={handleOpenApp}>
            {OPEN_CTA}
          </button>
          <ActionLink
            className="banner-close-button"
            onClick={handleClose}
            content={<CloseButtonIcon className="close-button-icon" />}
            label="Close"
          />
        </Box>
      </Box>
    </Box>
  );
};
